import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import WorkOrderDetail from './WorkOrderDetail';
import MaintenanceTaskDetail from './MaintenanceTaskDetail';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Clock,
  Bell,
  Settings,
  Wrench,
  CheckSquare,
  AlertTriangle,
  User,
  Building2,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

function DailyTasks({ user }) {
  const { t } = useLanguage();
  
  const COLUMNS = [
    { id: 'Scheduled', title: t('scheduled'), color: 'status-scheduled' },
    { id: 'In Progress', title: t('inProgress'), color: 'status-in-progress' },
    { id: 'Completed', title: t('completed'), color: 'status-completed' },
    { id: 'On Hold', title: t('onHold'), color: 'status-on-hold' }
  ];
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskInstances, setTaskInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;


  // Generate and load today's task instances
  const loadTodayTasks = async () => {
    try {
      setLoading(true);
      
      // First, migrate existing task instances to have assignees
      const migrateResponse = await fetch(`${API}/task-instances/migrate-assignees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (migrateResponse.ok) {
        const migrateResult = await migrateResponse.json();
        console.log('Migration result:', migrateResult);
        console.log('Migration sample_update:', migrateResult.sample_update);
      } else {
        console.error('Migration failed:', await migrateResponse.text());
      }
      
      // Then generate today's task instances (this also cleans up duplicates)
      await fetch(`${API}/task-instances/generate-today`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Then load the instances
      const response = await fetch(`${API}/task-instances/today`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const instances = await response.json();
        console.log('Loaded task instances for today:', instances.length);
        
        // Debug: Log full details of first instance
        if (instances.length > 0) {
          console.log('First instance full data:', JSON.stringify(instances[0], null, 2));
          console.log('First instance assignees:', instances[0].assignees);
          console.log('First instance assignee_names:', instances[0].assignee_names);
        }
        
        setTaskInstances(instances);
      } else {
        console.error('Failed to fetch task instances');
        toast.error('Failed to load today\'s tasks');
      }
    } catch (error) {
      console.error('Error loading task instances:', error);
      toast.error('Failed to load today\'s tasks');
    } finally {
      setLoading(false);
    }
  };

  const cleanupDuplicates = async () => {
    try {
      const response = await fetch(`${API}/task-instances/cleanup-duplicates`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        // Reload tasks after cleanup
        loadTodayTasks();
      } else {
        toast.error('Failed to cleanup duplicates');
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      toast.error('Failed to cleanup duplicates');
    }
  };

  useEffect(() => {
    // Sync missing fields from work orders to task instances (migration)
    const syncTaskInstanceFields = async () => {
      try {
        await fetch(`${API}/task-instances/sync-fields`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('Task instance fields synced');
      } catch (error) {
        console.error('Error syncing task instance fields:', error);
      }
    };
    
    syncTaskInstanceFields();
    loadTodayTasks();

    // Listen for work order updates from other pages (maintenance/repair task creation/updates)
    const handleWorkOrderUpdate = () => {
      console.log('DailyTasks: Received workOrderUpdated event, reloading tasks');
      loadTodayTasks();
    };

    // Listen for work order status changes from Repair Work Orders page
    const handleWorkOrderStatusChange = (event) => {
      const { workOrderId, status } = event.detail;
      console.log('DailyTasks: Received status change from KanbanBoard', workOrderId, status);
      
      // If a work order was completed on KanbanBoard, remove its task instance from DailyTasks
      if (status === 'Completed') {
        setTaskInstances(prevInstances =>
          prevInstances.filter(t => t.work_order_id !== workOrderId)
        );
        toast.success('Task completed from Repair Work Orders page');
      }
    };

    window.addEventListener('workOrderUpdated', handleWorkOrderUpdate);
    window.addEventListener('workOrderStatusChanged', handleWorkOrderStatusChange);
    
    return () => {
      window.removeEventListener('workOrderUpdated', handleWorkOrderUpdate);
      window.removeEventListener('workOrderStatusChanged', handleWorkOrderStatusChange);
    };
  }, [API]);


  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e, taskInstance) => {
    setDraggedItem(taskInstance);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(columnId);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverColumn(null);
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedItem || draggedItem.status === columnId) {
      setDraggedItem(null);
      return;
    }

    try {
      // If dropping into Completed column, complete the task
      if (columnId === 'Completed') {
        const response = await fetch(`${API}/task-instances/${draggedItem.id}/complete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          
          // Update status to Completed temporarily
          setTaskInstances(prevInstances =>
            prevInstances.map(t =>
              t.id === draggedItem.id ? { ...t, status: 'Completed' } : t
            )
          );
          
          toast.success('Task completed and saved to history. Removing in 2 seconds...');
          
          // Notify calendar to reload (to update completed task display)
          window.dispatchEvent(new CustomEvent('taskCompleted'));
          
          // If a repair work order was deleted (one-time task), notify Repair Work Orders page
          if (result.work_order_deleted) {
            window.dispatchEvent(new CustomEvent('workOrderDeleted', {
              detail: { workOrderId: result.work_order_id }
            }));
            console.log('Repair work order deleted:', result.work_order_id);
          }
          
          // Wait 2 seconds, then remove from view
          setTimeout(() => {
            setTaskInstances(prevInstances =>
              prevInstances.filter(t => t.id !== draggedItem.id)
            );
            toast.success('Task removed from Daily Tasks');
          }, 2000);
        } else {
          toast.error('Failed to complete task');
        }
      } else {
        // Just update status for other columns
        const response = await fetch(`${API}/task-instances/${draggedItem.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: columnId })
        });

        if (response.ok) {
          setTaskInstances(prevInstances =>
            prevInstances.map(t =>
              t.id === draggedItem.id ? { ...t, status: columnId } : t
            )
          );
          toast.success(`Task moved to ${columnId}`);
        } else {
          toast.error('Failed to update task status');
        }
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }

    setDraggedItem(null);
  };

  const getInstancesForColumn = (columnId) => {
    return taskInstances.filter(instance => instance.status === columnId);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'priority-critical',
      'High': 'priority-high',
      'Medium': 'priority-medium',
      'Low': 'priority-low'
    };
    return colors[priority] || 'bg-gray-500';
  };

  const getTypeIcon = (type) => {
    return type === 'PM' ? Settings : Wrench;
  };

  const getTypeColor = (type) => {
    return type === 'PM' 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const formatTime = (timeString) => {
    if (!timeString || typeof timeString !== 'string' || !timeString.includes(':')) {
      return 'Time not set';
    }
    
    const [hour, minute] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hour), parseInt(minute));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleTaskClick = (taskInstance) => {
    setSelectedTask(taskInstance);
  };

  const handleTaskUpdate = (updatedTask) => {
    // Reload today's tasks after update
    loadTodayTasks();
  };

  const handleDeleteTask = async (e, taskInstance) => {
    e.stopPropagation(); // Prevent opening the task detail
    
    if (!window.confirm('Are you sure you want to delete this task instance?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/task-instances/${taskInstance.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setTaskInstances(prev => prev.filter(t => t.id !== taskInstance.id));
        toast.success('Task instance deleted successfully');
      } else {
        toast.error('Failed to delete task instance');
      }
    } catch (error) {
      console.error('Failed to delete task instance:', error);
      toast.error('Failed to delete task instance');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="daily-tasks">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-tasks">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Clock className="w-6 h-6 mr-2 text-blue-600" />
              {t('dailyTasksPage')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('dragDropTasks')} • {t('totalTasks')}: {taskInstances.length}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              {currentTime.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
              })}
            </Badge>
            <Button
              onClick={loadTodayTasks}
              variant="outline"
              size="sm"
              className="flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {currentTime.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {COLUMNS.map(column => {
          const columnInstances = getInstancesForColumn(column.id);
          const isDragOver = dragOverColumn === column.id;
          
          return (
            <div key={column.id} className="flex flex-col">
              {/* Column Header */}
              <div className={`bg-white rounded-t-lg border-t-4 ${column.color} p-4 border-l border-r border-gray-200`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{column.title}</h3>
                  <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                    {columnInstances.length}
                  </Badge>
                </div>
              </div>

              {/* Column Content */}
              <div
                className={`
                  flex-1 bg-gray-50 border-l border-r border-b border-gray-200 rounded-b-lg p-3 min-h-96
                  ${isDragOver ? 'bg-blue-50 border-blue-300 border-2 border-dashed' : ''}
                `}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <div className="space-y-3">
                  {columnInstances.map(instance => {
                    const Icon = getTypeIcon(instance.work_order_type);
                    
                    return (
                      <Card
                        key={instance.id}
                        className={`
                          cursor-pointer hover:shadow-lg transition-all duration-200
                          ${draggedItem?.id === instance.id ? 'opacity-50 rotate-2' : ''}
                        `}
                        draggable
                        onDragStart={(e) => handleDragStart(e, instance)}
                        onClick={() => handleTaskClick(instance)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <Icon className={`w-4 h-4 ${instance.work_order_type === 'PM' ? 'text-green-600' : 'text-blue-600'}`} />
                                <Badge className={`text-xs px-2 py-0.5 ${instance.work_order_type === 'PM' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {instance.work_order_type === 'PM' ? 'Maintenance' : 'Repair'}
                                </Badge>
                                <Badge className={`text-xs px-2 py-0.5 ${getPriorityColor(instance.priority)}`}>
                                  {instance.priority}
                                </Badge>
                                {instance.frequency && (
                                  <Badge variant="outline" className="text-xs px-2 py-0.5">
                                    {instance.frequency}
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                {instance.title}
                              </h4>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2 text-xs text-gray-600">
                            {/* Time */}
                            {instance.scheduled_time && (
                              <div className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatTime(instance.scheduled_time)}
                              </div>
                            )}
                            
                            {/* Scheduled Start Date/Time */}
                            {instance.scheduled_start && (
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                <span className="font-medium">Start:</span>
                                <span className="ml-1">
                                  {new Date(instance.scheduled_start).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </span>
                              </div>
                            )}
                            
                            {/* Scheduled End Date/Time */}
                            {instance.scheduled_end && (
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                <span className="font-medium">End:</span>
                                <span className="ml-1">
                                  {new Date(instance.scheduled_end).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </span>
                              </div>
                            )}
                            
                            {/* Assignees - Multiple */}
                            <div className="flex items-center text-xs text-gray-600">
                              <User className="w-3 h-3 mr-1" />
                              {instance.assignee_names && instance.assignee_names.length > 0 
                                ? instance.assignee_names.join(', ') 
                                : (instance.assignee_name || 'Unassigned')}
                            </div>
                            
                            {/* Department */}
                            {instance.department_name && (
                              <div className="flex items-center">
                                <Building2 className="w-3 h-3 mr-1" />
                                {instance.department_name}
                              </div>
                            )}
                            
                            {/* Machine */}
                            {instance.machine_name && (
                              <div className="flex items-center">
                                <Settings className="w-3 h-3 mr-1" />
                                {instance.machine_name}
                              </div>
                            )}
                            
                            {/* Checklist count */}
                            {instance.checklist && instance.checklist.length > 0 && (
                              <div className="flex items-center text-blue-600">
                                <CheckSquare className="w-3 h-3 mr-1" />
                                {instance.checklist.filter(item => item.completed).length}/{instance.checklist.length} items
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* Empty state */}
                  {columnInstances.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Clock className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-sm">No tasks</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <>
          {selectedTask.work_order_type === 'PM' ? (
            <MaintenanceTaskDetail
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={handleTaskUpdate}
              user={user}
              readOnly={true}
            />
          ) : (
            <WorkOrderDetail
              workOrder={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={handleTaskUpdate}
              user={user}
              readOnly={true}
            />
          )}
        </>
      )}
    </div>
  );
}

export default DailyTasks;